import { useEffect, useState, useMemo, useCallback } from 'react';
import { Map, MapMarker, Polyline, useKakaoLoader } from 'react-kakao-maps-sdk';
import styles from './KakaoMap.module.css';

const FALLBACK_CENTER = { lat: 37.5665, lng: 126.9780 }; // Seoul City Hall

/**
 * KakaoMap for trip routes
 * places: [{ name, latitude/longitude or lat/lng, address }]
 */
export default function KakaoMap({ places = [], level = 4 }) {
    useKakaoLoader({ appkey: process.env.REACT_APP_KAKAOMAP_KEY, libraries: ['services'] });
    const [center, setCenter] = useState(FALLBACK_CENTER);
    const [map, setMap] = useState(null);
    const [resolved, setResolved] = useState([]);

    // geolocation: center near user
    useEffect(() => {
        if (!('geolocation' in navigator)) return;
        let cancelled = false;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                if (cancelled) return;
                setCenter({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                });
            },
            (err) => console.error('Geolocation error:', err),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
        return () => { cancelled = true; };
    }, []);

    // Build markers from places (lat/lng or address)
    const explicitMarkers = useMemo(() => {
        return (Array.isArray(places) ? places : [])
            .map((p, idx) => {
                const lat = Number(p.latitude ?? p.lat);
                const lng = Number(p.longitude ?? p.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
                return {
                    id: idx,
                    title: p.name || p.title || `장소 ${idx + 1}`,
                    position: { lat, lng },
                };
            })
            .filter(Boolean);
    }, [places]);

    const needGeocode = useMemo(() => {
        return (Array.isArray(places) ? places : [])
            .map((p, idx) => ({
                idx,
                query: p.address || p.addr || p.name,
                hasCoords: Number.isFinite(Number(p.latitude ?? p.lat)) && Number.isFinite(Number(p.longitude ?? p.lng)),
                raw: p,
            }))
            .filter((p) => !p.hasCoords && p.query);
    }, [places]);

    const geocodeMissing = useCallback(() => {
        if (!map || !window.kakao || needGeocode.length === 0) return Promise.resolve([]);
        const { kakao } = window;
        const geocoder = new kakao.maps.services.Geocoder();
        const tasks = needGeocode.map(({ idx, query, raw }) => new Promise((resolve) => {
            geocoder.addressSearch(query, (result, status) => {
                if (status === kakao.maps.services.Status.OK && result?.[0]) {
                    const { x, y } = result[0];
                    resolve({
                        id: idx,
                        title: raw.name || raw.title || query,
                        position: { lat: Number(y), lng: Number(x) },
                    });
                } else {
                    resolve(null);
                }
            });
        }));
        return Promise.all(tasks).then((vals) => vals.filter(Boolean));
    }, [map, needGeocode]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const fromGeo = await geocodeMissing();
            const markers = [...explicitMarkers, ...fromGeo].sort((a, b) => a.id - b.id);
            if (cancelled) return;
            setResolved(markers);
            if (!map || markers.length === 0 || !window.kakao) return;
            const { kakao } = window;
            if (markers.length === 1) {
                map.setLevel(level);
                map.setCenter(new kakao.maps.LatLng(markers[0].position.lat, markers[0].position.lng));
            } else {
                const bounds = new kakao.maps.LatLngBounds();
                markers.forEach((m) => bounds.extend(new kakao.maps.LatLng(m.position.lat, m.position.lng)));
                map.setBounds(bounds);
            }
        })();
        return () => { cancelled = true; };
    }, [explicitMarkers, geocodeMissing, map, level]);

    const path = useMemo(() => resolved.map((m) => m.position), [resolved]);

    return (
        <div className={styles.map__wrapper}>
            <Map
                center={center}
                isPanto
                level={level}
                className={styles.kakaoMap}
                style={{ width: '100%', height: '100%' }}
                onCreate={setMap}
                draggable={false}
                zoomable={false}
                keyboardShortcuts={false}
            >
                {resolved.map((m) => (
                    <MapMarker
                        key={m.id}
                        position={m.position}
                        title={m.title}
                    />
                ))}
                {path.length >= 2 && (
                    <Polyline
                        path={path}
                        strokeWeight={4}
                        strokeColor="#FF5C00"
                        strokeOpacity={0.9}
                        strokeStyle="solid"
                    />
                )}
            </Map>
        </div>
    );
}
